// import { Component, inject } from '@angular/core';
// import { RouterOutlet } from '@angular/router';
// import { TelegramService } from './services/telegram.service';

// @Component({
//   selector: 'app-root',
//   standalone: true,
//   imports: [RouterOutlet],
//   templateUrl: './app.component.html',
//   styleUrl: './app.component.less',
// })
// export class AppComponent {
//   title = 'berneknows-pwa';
//   data;
//   user;
//   telegram = inject(TelegramService);
//   constructor() {
//     this.telegram.ready();
//     this.data = JSON.stringify(this.telegram.tg)?.split(',');
//     this.user = JSON.stringify(this.telegram.tgUser)?.split(',');
//   }
// }

import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { TuiRoot } from '@taiga-ui/core';
import { WA_LOCAL_STORAGE, WA_WINDOW } from '@ng-web-apis/common';
import { TUI_DARK_MODE, TUI_DARK_MODE_KEY } from '@taiga-ui/core';
import { TuiLanguageSwitcherService } from '@taiga-ui/i18n/utils';
import { TranslocoService } from '@jsverse/transloco';
import { TelegramService } from './services/telegram.service';
import { UserService } from './services';
import { LANGUAGES } from './const';


@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, TuiRoot],
  templateUrl: './app.component.html',
  styleUrl: './app.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private readonly consentStorageKey = 'my3ego-consent-accepted-v1';
  private readonly userLangStorageKey = 'bk_lang';
  private readonly key = inject(TUI_DARK_MODE_KEY);
  private readonly storage = inject(WA_LOCAL_STORAGE);
  private readonly media = inject(WA_WINDOW).matchMedia(
    '(prefers-color-scheme: dark)'
  );
  protected readonly darkMode = inject(TUI_DARK_MODE);
  private readonly telegram = inject(TelegramService);
  private readonly translocoService = inject(TranslocoService);
  private readonly switcher = inject(TuiLanguageSwitcherService);
  private readonly userService = inject(UserService);
  private activeLang: string = 'ru';
  protected showConsentModal = false;
  protected isConsentChecked = false;
  private readonly docsByLanguage: Record<
    string,
    { folder: string; privacy: string; terms: string }
  > = {
    ru: {
      folder: 'ru',
      privacy: 'Политика конфиденциальности Мои3эго.pdf',
      terms: 'Пользовательское соглашение Мои3эго.pdf',
    },
    gb: {
      folder: 'gb',
      privacy: 'Мои3эго Privacy Policy (EN).pdf',
      terms: 'Мои3эго Terms of Service (EN).pdf',
    },
    es: {
      folder: 'gb',
      privacy: 'Мои3эго Privacy Policy (EN).pdf',
      terms: 'Мои3эго Terms of Service (EN).pdf',
    },
    de: {
      folder: 'gb',
      privacy: 'Мои3эго Privacy Policy (EN).pdf',
      terms: 'Мои3эго Terms of Service (EN).pdf',
    },
    ar: {
      folder: 'gb',
      privacy: 'Мои3эго Privacy Policy (EN).pdf',
      terms: 'Мои3эго Terms of Service (EN).pdf',
    },
    zh: {
      folder: 'gb',
      privacy: 'Мои3эго Privacy Policy (EN).pdf',
      terms: 'Мои3эго Terms of Service (EN).pdf',
    },
    id: {
      folder: 'gb',
      privacy: 'Мои3эго Privacy Policy (EN).pdf',
      terms: 'Мои3эго Terms of Service (EN).pdf',
    },
    ja: {
      folder: 'gb',
      privacy: 'Мои3эго Privacy Policy (EN).pdf',
      terms: 'Мои3эго Terms of Service (EN).pdf',
    },
    kr: {
      folder: 'gb',
      privacy: 'Мои3эго Privacy Policy (EN).pdf',
      terms: 'Мои3эго Terms of Service (EN).pdf',
    },
    tr: {
      folder: 'gb',
      privacy: 'Мои3эго Privacy Policy (EN).pdf',
      terms: 'Мои3эго Terms of Service (EN).pdf',
    },
    pt: {
      folder: 'gb',
      privacy: 'Мои3эго Privacy Policy (EN).pdf',
      terms: 'Мои3эго Terms of Service (EN).pdf',
    },
  };
  private readonly cdr = inject(ChangeDetectorRef);

  constructor() {}

  protected reset(): void {
    this.darkMode.set(this.media.matches);
    this.storage.removeItem(this.key);
  }

  ngOnInit(): void {
    // В тестах и на очень ранних стадиях загрузки переводов метод `load()`
    // может отсутствовать (мок). Поэтому делаем вызов безопасно.
    const shouldShow =
      this.storage.getItem(this.consentStorageKey) !== 'true';
    this.showConsentModal = false;

    const storedLang = this.storage.getItem(this.userLangStorageKey);
    const hasStoredLang =
      typeof storedLang === 'string' && storedLang.trim().length > 0;

    const tgUser = this.telegram.getTgUser();
    const mappedFromTg = tgUser?.language_code
      ? this.normalizeToSupportedLang(
          this.mapTelegramLanguage(tgUser.language_code),
        )
      : null;
    const targetLang = hasStoredLang
      ? this.normalizeToSupportedLang(storedLang)
      : mappedFromTg ?? null;

    if (!targetLang) {
      // Keep previous behavior for tests/edge cases:
      // when tgUser is null -> log, when tgUser has no language_code -> do nothing.
      if (!tgUser) {
        console.log('Telegram user not found. Using default language.');
      }
      return;
    }

    this.activeLang = targetLang;
    const selected = LANGUAGES.find(
      (lang) => this.getLocaleFromLanguageKey(lang.key) === targetLang,
    );

    if (!selected) return;

    this.translocoService.setActiveLang(targetLang);
    this.switcher.setLanguage(selected.title);

    const user = Object.assign({}, this.userService.user);
    user.lang = targetLang;
    user.synch = false;
    this.userService.user = user;
    this.userService.saveUser();

    const translocoAny = this.translocoService as unknown as {
      load?: (lang: string) => {
        subscribe: (cb: () => void) => void;
      };
      translate?: (key: string) => string;
    };

    const maybeLoad = translocoAny.load?.bind(this.translocoService);

    if (typeof maybeLoad === 'function') {
      maybeLoad(targetLang).subscribe(() => {
        this.showConsentModal = shouldShow && typeof translocoAny.translate === 'function';
        this.cdr.markForCheck();
      });
      return;
    }

    // Fallback: в проде `load()` обычно существует, в тестах — нет.
    // Если `translate()` отсутствует (как в моках unit-тестов), не рендерим попап,
    // чтобы не падать на обращениях к переводам.
    this.showConsentModal = shouldShow && typeof translocoAny.translate === 'function';
  }

  protected t(key: string): string {
    return this.translocoService.translate(key);
  }

  protected onConsentCheckboxChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.isConsentChecked = target.checked;
  }

  protected acceptConsent(): void {
    if (!this.isConsentChecked) {
      return;
    }

    this.storage.setItem(this.consentStorageKey, 'true');
    this.showConsentModal = false;
  }

  protected get privacyPolicyUrl(): string {
    const docs = this.getDocsByLanguage();
    return this.buildDocUrl(docs.folder, docs.privacy);
  }

  protected get termsOfServiceUrl(): string {
    const docs = this.getDocsByLanguage();
    return this.buildDocUrl(docs.folder, docs.terms);
  }

  private mapTelegramLanguage(value: string): string {
    const normalized = value.toLowerCase();
    if (normalized.startsWith('ru')) return 'ru';
    if (normalized.startsWith('de')) return 'de';
    if (normalized.startsWith('es')) return 'es';
    if (normalized.startsWith('pt')) return 'pt';
    if (normalized.startsWith('tr')) return 'tr';
    if (normalized.startsWith('ar')) return 'ar';
    if (normalized.startsWith('zh')) return 'zh';
    if (normalized.startsWith('ja') || normalized.startsWith('jp')) return 'ja';
    if (normalized.startsWith('ko') || normalized.startsWith('kr')) return 'kr';
    if (normalized.startsWith('id') || normalized.startsWith('in')) return 'id';
    if (normalized.startsWith('en')) return 'gb';
    if (normalized.startsWith('gb')) return 'gb';
    return 'ru';
  }

  private normalizeToSupportedLang(value: string): string {
    const normalized = value.toLowerCase();
    if (normalized.startsWith('ru')) return 'ru';
    if (normalized.startsWith('es')) return 'es';
    if (normalized.startsWith('gb')) return 'gb';
    if (normalized.startsWith('ja') || normalized.startsWith('jp')) return 'ja';
    if (normalized.startsWith('kr') || normalized.startsWith('ko')) return 'kr';
    if (normalized.startsWith('id') || normalized.startsWith('in')) return 'id';
    if (normalized.startsWith('de')) return 'de';
    if (normalized.startsWith('pt')) return 'pt';
    if (normalized.startsWith('tr')) return 'tr';
    if (normalized.startsWith('ar')) return 'ar';
    if (normalized.startsWith('zh')) return 'zh';
    return 'ru';
  }

  private getLocaleFromLanguageKey(value: string): string {
    const normalized = value.toLowerCase();
    if (normalized === 'sa') return 'ar';
    if (normalized === 'cn') return 'zh';
    if (normalized === 'jp') return 'ja';
    if (normalized === 'ko') return 'kr';
    return normalized;
  }

  private getDocsByLanguage(): { folder: string; privacy: string; terms: string } {
    return this.docsByLanguage[this.activeLang] ?? this.docsByLanguage['ru'];
  }

  private buildDocUrl(language: string, fileName: string): string {
    return (
      '/' +
      ['assets', 'i18n', language, 'landing', 'doc', fileName]
        .map((part) => encodeURIComponent(part))
        .join('/')
    );
  }
}
